import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Send, RefreshCw, Mail, AlertTriangle, Clock, ChevronDown, Paperclip } from 'lucide-react';
import { api, ApiError } from '../../lib/api';

// The email thread between NexSav and whoever filed a ticket / edit request,
// shown and answered inside that item's window. Every message is read from the
// portal's own records, so the whole thread is here even though it is sent
// through a mailbox (support@) that not everyone has in their own Gmail.
//
// kind: 'ticket' | 'edit_request'. onThread(thread) reports status changes
// (Awaiting reply, New reply) so the window and the list stay in step.

const ENDPOINT = { ticket: 'tickets', edit_request: 'edit-requests' };

function when(iso) {
  if (!iso) return '';
  const d = new Date(String(iso).replace(' ', 'T') + (String(iso).endsWith('Z') ? '' : 'Z'));
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

function MessageBubble({ m }) {
  const [showQuoted, setShowQuoted] = useState(false);
  const outbound = m.direction === 'outbound';
  const who = outbound
    ? `NexSav Support${m.sentByName ? ` · sent by ${m.sentByName.split(/\s+/)[0]}` : ''}`
    : (m.from?.name || m.from?.email || 'Unknown sender');
  // On our own emails the only hidden part is the footer: nothing worth a toggle.
  const hasQuoted = !outbound && (m.bodyText || '').trim() !== (m.visibleText || '').trim();
  return (
    <li
      className={`rounded-lg border px-3.5 py-2.5 ${outbound ? 'border-[#B0FC38]/20 bg-[#B0FC38]/[0.04] ml-6' : 'border-white/[0.08] bg-white/[0.03] mr-6'}`}
      data-testid={`message-${m.direction}`}
    >
      <div className="flex items-baseline justify-between gap-3 mb-1">
        <span className={`text-[12px] font-medium ${outbound ? 'text-[#B0FC38]' : 'text-white'}`}>{who}</span>
        <time className="text-[11px] text-gray-500 tabular-nums whitespace-nowrap">{when(m.sentAt)}</time>
      </div>
      {!outbound && m.from?.email && m.from?.name && <div className="text-[11px] text-gray-500 -mt-0.5 mb-1">{m.from.email}</div>}
      {outbound && m.to?.length > 0 && <div className="text-[11px] text-gray-500 -mt-0.5 mb-1">To {m.to.join(', ')}</div>}
      <div className="text-[13px] text-gray-200 leading-relaxed whitespace-pre-wrap break-words">{m.visibleText || m.bodyText}</div>
      {m.attachments?.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {m.attachments.map((a) => (
            <a
              key={a.id}
              href={`/api/admin/communications/attachments/${a.id}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded border border-white/[0.08] text-[11px] text-gray-300 hover:text-white"
            >
              <Paperclip className="w-3 h-3" aria-hidden="true" />{a.filename}
            </a>
          ))}
        </div>
      )}
      {hasQuoted && (
        <div className="mt-1.5">
          <button type="button" onClick={() => setShowQuoted((v) => !v)} className="inline-flex items-center gap-1 text-[11px] text-gray-500 hover:text-gray-300" aria-expanded={showQuoted}>
            <ChevronDown className={`w-3 h-3 transition-transform ${showQuoted ? 'rotate-180' : ''}`} aria-hidden="true" />
            {showQuoted ? 'Hide earlier messages' : 'Show earlier messages'}
          </button>
          {showQuoted && <div className="mt-1.5 text-[12px] text-gray-500 whitespace-pre-wrap break-words border-l-2 border-white/[0.08] pl-3">{m.bodyText}</div>}
        </div>
      )}
    </li>
  );
}

function PendingRow({ j }) {
  const failed = j.status === 'failed';
  return (
    <li className={`rounded-lg border px-3.5 py-2.5 ml-6 ${failed ? 'border-red-500/30 bg-red-500/[0.05]' : 'border-white/[0.08] bg-white/[0.02]'}`} data-testid={`pending-${j.status}`}>
      <div className="flex items-center gap-1.5 text-[12px] mb-1">
        {failed ? <AlertTriangle className="w-3.5 h-3.5 text-red-300" aria-hidden="true" /> : <Clock className="w-3.5 h-3.5 text-gray-400" aria-hidden="true" />}
        <span className={failed ? 'text-red-300' : 'text-gray-400'}>
          {failed ? `Not sent${j.lastError ? `: ${j.lastError}` : ''}` : j.status === 'unknown' ? 'Checking it was delivered…' : 'Sending…'}
        </span>
      </div>
      <div className="text-[13px] text-gray-400 whitespace-pre-wrap break-words">{String(j.bodyText || '').split('\n--\n')[0]}</div>
    </li>
  );
}

export default function RequestConversation({ kind, itemId, onThread }) {
  const [thread, setThread] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [text, setText] = useState('');
  const [awaitReply, setAwaitReply] = useState(true);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState('');
  const [notice, setNotice] = useState('');
  const onThreadRef = useRef(onThread);
  onThreadRef.current = onThread;
  const base = `/api/admin/${ENDPOINT[kind]}/${itemId}/conversation`;

  const apply = useCallback((t) => {
    setThread(t);
    onThreadRef.current?.(t);
  }, []);

  const load = useCallback(async ({ quiet = false, sync = true } = {}) => {
    if (!quiet) setLoading(true);
    setLoadError('');
    try {
      const res = await api.get(sync ? base : `${base}?sync=0`);
      apply(res.thread);
    } catch (err) {
      setLoadError(err?.message || 'Could not load the conversation');
    } finally {
      if (!quiet) setLoading(false);
    }
  }, [base, apply]);

  // Open: load (which starts a mail sync in the background), then look again a
  // few seconds later so a reply that sync just pulled in appears by itself.
  useEffect(() => {
    load();
    const t = setTimeout(() => load({ quiet: true, sync: false }), 6000);
    return () => clearTimeout(t);
  }, [load]);

  const send = async (e) => {
    e.preventDefault();
    if (sending || !text.trim()) return;
    setSending(true);
    setSendError('');
    setNotice('');
    try {
      const res = await api.post(base, {
        text,
        await_reply: awaitReply,
        client_token: `req-${kind}-${itemId}-${Date.now()}`,
      });
      apply(res.thread);
      setText('');
      setNotice(res.job?.status === 'unknown' ? 'Sent. Gmail is confirming delivery.' : awaitReply ? 'Sent. Marked Awaiting reply.' : 'Sent.');
    } catch (err) {
      if (err instanceof ApiError && err.data?.thread) apply(err.data.thread);
      setSendError(err instanceof ApiError ? err.message : 'Network error, please try again.');
    } finally {
      setSending(false);
    }
  };

  const who = thread?.submitter?.name || thread?.submitter?.email || 'the submitter';
  const messages = thread?.messages || [];
  const pending = thread?.pending || [];

  return (
    <section className="pt-3 border-t border-white/[0.06]" aria-labelledby={`conv-${itemId}-title`} data-testid="request-conversation">
      <div className="flex items-center justify-between gap-3 mb-2">
        <h3 id={`conv-${itemId}-title`} className="text-[10px] font-medium text-gray-500 tracking-wider uppercase">
          Conversation with {who}
        </h3>
        <button
          type="button"
          onClick={() => load({ quiet: true })}
          className="inline-flex items-center gap-1 text-[11px] text-gray-500 hover:text-white"
          title="Check for new replies"
        >
          <RefreshCw className="w-3 h-3" aria-hidden="true" /> Refresh
        </button>
      </div>

      {loadError && <div className="mb-2 text-[12px] text-red-300" role="alert">{loadError}</div>}

      {loading && !thread ? (
        <div className="h-12 rounded-lg bg-white/[0.03] animate-pulse" />
      ) : messages.length === 0 && pending.length === 0 ? (
        <p className="text-[12px] text-gray-500 mb-3">
          No emails yet. Ask {who} a question below. They’ll get it from NexSav Support, and their reply lands back here.
        </p>
      ) : (
        <ol className="flex flex-col gap-2 mb-3 list-none p-0 m-0" aria-label="Messages, oldest first">
          {messages.map((m) => <MessageBubble key={m.id} m={m} />)}
          {pending.map((j) => <PendingRow key={j.id} j={j} />)}
        </ol>
      )}

      <form onSubmit={send} className="rounded-lg border border-white/[0.08] bg-white/[0.02] p-3 flex flex-col gap-2.5">
        <div className="flex items-center gap-2 text-[12px] text-gray-400 flex-wrap">
          <Mail className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />
          <span>From <span className="text-gray-200">{thread?.sender?.name || 'NexSav Support'}</span> &lt;{thread?.sender?.email || 'support@nexsav.net'}&gt;</span>
        </div>
        {thread?.sender && !thread.sender.ready && thread.sender.problem && (
          <div className="text-[12px] text-amber-200/90 flex items-start gap-1.5" role="note">
            <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-px" aria-hidden="true" />{thread.sender.problem}
          </div>
        )}
        {/* Always the person who filed this item: not editable. */}
        <div className="flex items-center gap-2 text-[12px] text-gray-400 flex-wrap" data-testid="conversation-to">
          <span className="w-6 shrink-0">To</span>
          {!thread ? (
            <span className="text-gray-500">…</span>
          ) : thread.defaultTo?.length ? (
            <span className="text-gray-200">
              {thread.submitter?.name ? `${thread.submitter.name} ` : ''}&lt;{thread.defaultTo.join(', ')}&gt;
            </span>
          ) : (
            <span className="text-amber-200/90">This item has no submitter email address, so it can’t be emailed.</span>
          )}
        </div>
        <textarea
          id={`conv-${itemId}-text`}
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={4}
          placeholder={messages.length ? 'Write a reply…' : 'Ask a question or send an update…'}
          aria-label="Message"
          className="w-full px-3 py-2 rounded-md bg-white/[0.03] border border-white/[0.06] text-[13px] text-white placeholder-gray-600 focus:outline-none focus:ring-1 focus:ring-[#B0FC38]/30 resize-y min-h-[84px]"
        />
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <label className="inline-flex items-center gap-2 text-[12px] text-gray-300 select-none cursor-pointer">
            <input type="checkbox" checked={awaitReply} onChange={(e) => setAwaitReply(e.target.checked)} className="accent-[#B0FC38]" />
            Mark as Awaiting reply
          </label>
          <button
            type="submit"
            disabled={sending || !text.trim() || !thread || !thread.sender?.ready || !thread.defaultTo?.length}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#B0FC38] text-gray-900 text-[13px] font-semibold hover:bg-[#9BE22E] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Send className="w-3.5 h-3.5" aria-hidden="true" />{sending ? 'Sending…' : 'Send'}
          </button>
        </div>
        {sendError && <div className="text-[12px] text-red-300" role="alert">{sendError}</div>}
        {notice && <div className="text-[12px] text-[#B0FC38]" role="status">{notice}</div>}
      </form>
    </section>
  );
}
